import React from 'react';
import { FiDollarSign, FiTrendingUp } from 'react-icons/fi';
import { calculateTotalRevenuesEmployee } from '@/utils/financialCalculations';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  TextArea,
  InfoBox,
  HelpText,
  CalculatedValue,
  CalculatedLabel,
  CalculatedAmount
} from '../common/FormStyles';
import { IncomeInfoEmployee, SpouseIncomeEmployee } from '@/types/application-forms';
import { DocumentUpload, UploadedDocument } from '../common/DocumentUpload';
import { INCOME_DOCUMENTS } from '@/config/documentsBySection';

interface IncomeSectionProps {
  candidateData: Partial<IncomeInfoEmployee>;
  spouseData?: Partial<SpouseIncomeEmployee>;
  hasCoupleData: boolean;
  onCandidateChange: (data: Partial<IncomeInfoEmployee>) => void;
  onSpouseChange?: (data: Partial<SpouseIncomeEmployee>) => void;
  uploadedDocuments?: UploadedDocument[];
  onDocumentUpload?: (type: string, file: File | null) => void;
  onDocumentRemove?: (type: string) => void;
  readonly?: boolean;
}

export const IncomeSection: React.FC<IncomeSectionProps> = ({
  candidateData,
  spouseData,
  hasCoupleData,
  onCandidateChange,
  onSpouseChange,
  uploadedDocuments = [],
  onDocumentUpload,
  onDocumentRemove,
  readonly = false
}) => {


  const handleCandidateChange = (field: string, value: any) => {
    onCandidateChange({ ...(candidateData || {}), [field]: value });
  };

  const handleSpouseChange = (field: string, value: any) => {
    if (onSpouseChange) {
      onSpouseChange({ ...(spouseData || {}), [field]: value });
    }
  };

  // Calcul automatique du total des revenus utilisant la fonction utilitaire centralisée
  const totalRevenus = candidateData ? calculateTotalRevenuesEmployee(candidateData) : 0;
  const totalRevenusFoyer = totalRevenus + (spouseData?.revenus_mensuels || 0);

  // Mettre à jour le total calculé
  React.useEffect(() => {
    if (candidateData ) {
      // handleCandidateChange('monthly_income', totalRevenus);
      handleCandidateChange('total_revenus', totalRevenus);
    }
  }, [totalRevenus, candidateData]);

  return (
    <>
      <FormSection>
        <SectionTitle>
          <FiDollarSign />
          Revenus du Candidat
        </SectionTitle>

        <InfoBox $type="info">
          <p>
            Indiquez tous vos revenus mensuels nets (après impôts). Soyez le plus précis possible
            pour une évaluation juste de votre capacité de paiement.
          </p>
        </InfoBox>

        <SubSectionTitle>Salaire de Base</SubSectionTitle>

        <FormGroup>
          <Label $required>Salaire de base net (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={candidateData?.salaire_base_net || ''}
            onChange={e => handleCandidateChange('salaire_base_net', parseInt(e.target.value) || 0)}
            placeholder="Ex: 300000"
            disabled={readonly}
          />
          <HelpText>Montant net après déduction des impôts et cotisations</HelpText>
        </FormGroup>

        <SubSectionTitle>Indemnités et Primes Fixes</SubSectionTitle>

        <FormGrid>
          <FormGroup>
            <Label>Indemnité de transport (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={candidateData?.indemnite_transport || ''}
              onChange={e => handleCandidateChange('indemnite_transport', parseInt(e.target.value) || 0)}
              placeholder="Ex: 50000"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Indemnité de logement (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={candidateData?.indemnite_logement || ''}
              onChange={e => handleCandidateChange('indemnite_logement', parseInt(e.target.value) || 0)}
              placeholder="Ex: 75000"
              disabled={readonly}
            />
          </FormGroup>
        </FormGrid>

        <FormGroup>
          <Label>Autres indemnités fixes (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={candidateData?.autres_indemnites || ''}
            onChange={e => handleCandidateChange('autres_indemnites', parseInt(e.target.value) || 0)}
            placeholder="Ex: 25000"
            disabled={readonly}
          />
        </FormGroup>

        {(candidateData?.autres_indemnites || 0) > 0 && (
          <FormGroup>
            <Label>Description des autres indemnités</Label>
            <Input
              type="text"
              value={candidateData?.description_autres_indemnites || ''}
              onChange={e => handleCandidateChange('description_autres_indemnites', e.target.value)}
              placeholder="Ex: Prime d'ancienneté, prime de responsabilité..."
              disabled={readonly}
            />
          </FormGroup>
        )}

        <SubSectionTitle>Primes Variables</SubSectionTitle>

        <FormGroup>
          <Label>Primes variables (moyenne mensuelle en FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={candidateData?.primes_variables_moyenne || ''}
            onChange={e => handleCandidateChange('primes_variables_moyenne', parseInt(e.target.value) || 0)}
            placeholder="Ex: 100000"
            disabled={readonly}
          />
          <HelpText>Moyenne mensuelle des primes sur les 12 derniers mois</HelpText>
        </FormGroup>

        <SubSectionTitle>Autres Revenus Mensuels</SubSectionTitle>

        <FormGrid>
          <FormGroup>
            <Label>Commerce parallèle (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={candidateData?.commerce_parallele || ''}
              onChange={e => handleCandidateChange('commerce_parallele', parseInt(e.target.value) || 0)}
              placeholder="Ex: 50000"
              disabled={readonly}
            />
            <HelpText>Revenus d'une activité commerciale en parallèle</HelpText>
          </FormGroup>

          <FormGroup>
            <Label>Revenus de location (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={candidateData?.revenus_locatifs || ''}
              onChange={e => handleCandidateChange('revenus_locatifs', parseInt(e.target.value) || 0)}
              placeholder="Ex: 80000"
              disabled={readonly}
            />
            <HelpText>Loyers perçus si vous avez des biens en location</HelpText>
          </FormGroup>
        </FormGrid>

        <FormGrid>
          <FormGroup>
            <Label>Transferts de la diaspora (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={candidateData?.transferts_diaspora || ''}
              onChange={e => handleCandidateChange('transferts_diaspora', parseInt(e.target.value) || 0)}
              placeholder="Ex: 30000"
              disabled={readonly}
            />
            <HelpText>Moyenne mensuelle des transferts reçus de l'étranger</HelpText>
          </FormGroup>

          <FormGroup>
            <Label>Autres revenus (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={candidateData?.autres_revenus || ''}
              onChange={e => handleCandidateChange('autres_revenus', parseInt(e.target.value) || 0)}
              placeholder="Ex: 20000"
              disabled={readonly}
            />
          </FormGroup>
        </FormGrid>

        {(candidateData?.autres_revenus || 0) > 0 && (
          <FormGroup>
            <Label>Description des autres revenus</Label>
            <TextArea
              value={candidateData?.description_autres_revenus || ''}
              onChange={e => handleCandidateChange('description_autres_revenus', e.target.value)}
              placeholder="Décrivez la source de ces autres revenus..."
              disabled={readonly}
            />
          </FormGroup>
        )}

        <CalculatedValue>
          <CalculatedLabel>Total de vos revenus nets mensuels</CalculatedLabel>
          <CalculatedAmount>{totalRevenus.toLocaleString()} FCFA</CalculatedAmount>
        </CalculatedValue>

        {/* Documents justificatifs pour cette section */}
        {onDocumentUpload && onDocumentRemove && (
          <DocumentUpload
            sectionTitle="Revenus"
            documents={INCOME_DOCUMENTS}
            uploadedFiles={uploadedDocuments}
            onFileSelect={onDocumentUpload}
            onFileRemove={onDocumentRemove}
            readonly={readonly}
          />
        )}
      </FormSection>

      {hasCoupleData && (
        <FormSection>
          <SectionTitle>
            <FiTrendingUp />
            Revenus du Conjoint
          </SectionTitle>

          <FormGroup>
            <Label>Employeur / Activité</Label>
            <Input
              type="text"
              value={spouseData?.employeur_activite || ''}
              onChange={e => handleSpouseChange('employeur_activite', e.target.value)}
              placeholder="Nom de l'employeur ou type d'activité"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Type d'activité</Label>
            <select
              value={spouseData?.type_activite || ''}
              onChange={e => handleSpouseChange('type_activite', e.target.value)}
              disabled={readonly}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '16px'
              }}
            >
              <option value="">Sélectionnez...</option>
              <option value="Salariat">Salariat</option>
              <option value="Commerce">Commerce</option>
              <option value="Artisanat">Artisanat</option>
              <option value="Agriculture">Agriculture</option>
              <option value="Autre">Autre</option>
            </select>
          </FormGroup>

          <FormGroup>
            <Label>Revenus mensuels moyens (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={spouseData?.revenus_mensuels || ''}
              onChange={e => handleSpouseChange('revenus_mensuels', parseInt(e.target.value) || 0)}
              placeholder="Ex: 200000"
              disabled={readonly}
            />
          </FormGroup>

          <CalculatedValue>
            <CalculatedLabel>Total revenus du foyer</CalculatedLabel>
            <CalculatedAmount>{totalRevenusFoyer.toLocaleString()} FCFA</CalculatedAmount>
          </CalculatedValue>
        </FormSection>
      )}
    </>
  );
};
